import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import ThemeToggle from "../components/ThemeToggle";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import { Reveal, CountUp, AnimatedPoly, AnimatedBar, AnimatedDonut, AnimatedRing } from "../components/Animate";
import { C, Pill, StatCard, FeatureCard, StepNode, Arrow } from "../components/universities/primitives";
import UniversityFAQ from "../components/universities/UniversityFAQ";
import {
  ShieldCheck, Target, BarChart3, Users, Building2, GraduationCap,
  LayoutDashboard, Upload, Workflow, Handshake, FileSpreadsheet,
  ArrowRight, ArrowUpRight, CheckCircle2, AlertTriangle, X,
  ClipboardList, Briefcase, BookOpen, BrainCircuit, Lock, KeySquare,
  FileLock2, Plug, Cloud, BadgeCheck, ScrollText, Library,
  TrendingUp, BadgeDollarSign, Trophy, Sparkles, MessageSquare,
  Megaphone, UserPlus, Rocket, Globe, Mail,
} from "lucide-react";

// -------- The page --------
export default function UniversitiesPublic({ embedded = false }) {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  return (
    <div className="min-h-screen bg-white text-ink">
      {!embedded && <PublicNavBar primaryCta={{ to: "/signup?role=institute", label: "Request demo" }} />}

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: C.paper }}>
        <div className="absolute inset-0 -z-10 opacity-60"
          style={{ background: `radial-gradient(60% 50% at 90% 0%, ${C.navy}18 0%, transparent 60%), radial-gradient(60% 50% at 0% 100%, ${C.green}12 0%, transparent 60%)` }} />
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Pill color={C.navy}>For Universities</Pill>
            <h1 className="font-display font-black mt-5 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase" style={{ color: C.navyDeep }}>
              The Verified <span style={{ color: C.green }}>Employability</span><br />
              Platform for <span style={{ color: C.green }}>Universities</span>
            </h1>
            <p className="text-slate-600 mt-5 text-lg max-w-xl leading-relaxed">
              Empower your campus. Elevate placements. Prove outcomes. All on one intelligent ecosystem.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/signup?role=institute" data-testid="hero-cta-demo"
                className="px-6 py-3.5 rounded-lg font-semibold text-white inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDeep} 100%)` }}>
                Request a demo <ArrowUpRight size={16} />
              </Link>
              <a href="#how" className="px-6 py-3.5 rounded-lg font-semibold inline-flex items-center gap-2 border-2 transition-all duration-300 hover:-translate-y-1"
                style={{ borderColor: C.navy, color: C.navy }}>
                See how it works <ArrowRight size={16} />
              </a>
            </div>
          </div>

          {/* Right — dashboard preview */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-white shadow-2xl border p-5 relative overflow-hidden"
              style={{ borderColor: C.rule }}>
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-30" style={{ background: C.green }} />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest" style={{ color: C.navy }}>
                  <LayoutDashboard size={14} /> Campus Dashboard
                </div>
                <div className="grid grid-cols-4 gap-3 mt-4">
                  {[
                    { v: 12458, suffix: "",   label: "Total Students",       color: C.navy   },
                    { v: 48,    suffix: "%",  label: "Profile Completion",   color: C.green  },
                    { v: 27,    suffix: "%",  label: "Verified Credentials", color: C.purple },
                    { v: 356,   suffix: "",   label: "Active Employers",     color: C.orange },
                  ].map((s, i) => (
                    <div key={s.label} className="rounded-lg p-2.5 border" style={{ borderColor: C.rule }} data-testid={`hero-stat-${i}`}>
                      <div className="font-display font-black text-lg leading-tight" style={{ color: s.color }}>
                        <CountUp value={s.v} suffix={s.suffix} duration={1600} />
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>
                {/* animated line chart */}
                <div className="mt-4 rounded-lg p-3 border" style={{ borderColor: C.rule }}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Placement Overview</div>
                  <svg viewBox="0 0 200 50" className="w-full h-16 mt-1">
                    <AnimatedPoly points="0,40 25,35 50,32 75,28 100,22 125,20 150,14 175,10 200,8" color={C.navy}  delay={300} />
                    <AnimatedPoly points="0,45 25,42 50,38 75,32 100,28 125,22 150,18 175,12 200,9"  color={C.green} delay={600} />
                  </svg>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-lg p-3 border text-center" style={{ borderColor: C.rule }}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Student Match</div>
                    <div className="font-display font-black text-2xl mt-1" style={{ color: C.green }}>
                      <CountUp value={96} suffix="%" duration={1500} />
                    </div>
                  </div>
                  <div className="rounded-lg p-3 border" style={{ borderColor: C.rule }}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Top Skills</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {["SQL", "Python", "Data", "Viz"].map(s => (
                        <span key={s} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${C.green}15`, color: C.green }}>{s}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Promise bar */}
        <div className="border-t bg-white" style={{ borderColor: C.rule }}>
          <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              [ShieldCheck, "Verified Skills",  "Build Trust",                  C.green],
              [Target,      "Better Matches",   "Better Outcomes",              C.navy],
              [BarChart3,   "Data-Driven",      "Decisions",                    C.orange],
              [Users,       "Stronger Employability", "& Placements",          C.purple],
            ].map(([Icon, label, sub, color], i) => (
              <div key={label} className="group flex items-center gap-3" data-testid={`promise-${i}`}>
                <div className="w-11 h-11 rounded-lg grid place-items-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                  style={{ background: `${color}15`, color }}><Icon size={20} /></div>
                <div className="leading-tight">
                  <div className="font-bold text-sm" style={{ color: C.ink }}>{label}</div>
                  <div className="text-xs text-slate-500">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHALLENGES — BEFORE/AFTER */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.red}>Challenges universities face</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              The pre-SkillConnect <span style={{ color: C.red }}>reality.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mt-12 items-start">
            <ul className="lg:col-span-2 space-y-3">
              {[
                "Low placement conversion & visibility",
                "Manual & disconnected placement processes",
                "Unverified skills & incomplete student data",
                "Limited employer engagement & reach",
                "Lack of real-time analytics & outcome reporting",
              ].map((c, i) => (
                <li key={c} className="flex items-start gap-3 p-4 rounded-xl bg-white border transition-all hover:-translate-y-0.5 hover:shadow-sm"
                  style={{ borderColor: C.rule }} data-testid={`challenge-${i}`}>
                  <span className="w-9 h-9 shrink-0 rounded-lg grid place-items-center"
                    style={{ background: `${C.red}10`, color: C.red }}><X size={18} /></span>
                  <span className="text-sm font-medium pt-1.5" style={{ color: C.ink }}>{c}</span>
                </li>
              ))}
            </ul>

            <div className="rounded-2xl p-6 border-2 border-dashed text-center" style={{ borderColor: `${C.red}55`, background: `${C.red}05` }}>
              <AlertTriangle size={28} className="mx-auto" style={{ color: C.red }} />
              <div className="font-display font-black text-lg mt-3" style={{ color: C.red }}>BEFORE SKILLCONNECT</div>
              <div className="mt-4 text-sm space-y-2 text-slate-700">
                <div>Siloed Data</div>
                <div>Low Visibility</div>
                <div>High Effort</div>
                <div className="font-semibold" style={{ color: C.red }}>Low Outcomes</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW SKILLCONNECT HELPS */}
      <section id="how" className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.green}>How SkillConnect helps</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Automate · Connect · Analyze · <span style={{ color: C.green }}>Improve Outcomes</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-12">
            <FeatureCard icon={LayoutDashboard}  color={C.navy}   title="Campus Admin Dashboard"
              body="Real-time overview of students, placements & employers — one screen, no spreadsheets." />
            <FeatureCard icon={Upload}            color={C.green}  title="Student Import & Eligibility Filters"
              body="Bulk-import student data; filter by course, year, CGPA, skills & more." />
            <FeatureCard icon={Workflow}          color={C.orange} title="Placement Drive Management"
              body="Create drives, invite employers, manage applications & approvals — end to end." />
            <FeatureCard icon={Handshake}         color={C.purple} title="Employer Matching & Engagement"
              body="AI-powered matching with verified skills and employer needs — fewer mismatches." />
            <FeatureCard icon={FileSpreadsheet}   color={C.navy}   title="Outcomes & Accreditation Reports"
              body="Placement outcomes, NIRF/NAAC-ready reports & exports — accreditation made simple." />
          </div>
        </div>
      </section>

      {/* STUDENT LIFECYCLE */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.purple}>Student lifecycle</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              A seamless journey from profile to <span style={{ color: C.green }}>placement.</span>
            </h2>
            <p className="text-slate-600 mt-3">Seven stages. Verified skills. Real opportunities.</p>
          </div>
          <div className="mt-12 flex flex-wrap items-start justify-center gap-y-8 gap-x-2 md:gap-x-3">
            <StepNode n="01" icon={UserPlus}     title="Registration"        color={C.green} />
            <Arrow />
            <StepNode n="02" icon={ClipboardList} title="Profile Completion" color={C.green} />
            <Arrow />
            <StepNode n="03" icon={BadgeCheck}   title="Skill Verification"  color={C.navy} />
            <Arrow />
            <StepNode n="04" icon={Handshake}    title="Employer Matching"   color={C.orange} />
            <Arrow />
            <StepNode n="05" icon={Megaphone}    title="Placement Drive"     color={C.purple} />
            <Arrow />
            <StepNode n="06" icon={MessageSquare} title="Interviews"         color={C.navy} />
            <Arrow />
            <StepNode n="07" icon={Trophy}       title="Hiring Outcome"      color={C.green} />
          </div>
        </div>
      </section>

      {/* PLATFORM ECOSYSTEM */}
      <section className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.navy}>Platform ecosystem</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Four roles. <span style={{ color: C.green }}>One matching engine.</span>
            </h2>
          </div>
          <div className="mt-12 grid lg:grid-cols-5 gap-5 items-stretch">
            {[
              { role: "Students",     icon: GraduationCap, color: C.green,
                items: ["Build Profile", "Add Skills", "Earn Certificates", "Find Opportunities"] },
              { role: "Universities", icon: Building2,    color: C.navy,
                items: ["Manage Students", "Drive Placements", "Track Outcomes", "Improve Rankings"] },
              { role: "Companies",    icon: Briefcase,    color: C.orange,
                items: ["Post Jobs", "Filter Talent", "Shortlist Faster", "Hire Efficiently"] },
              { role: "Institutes",   icon: BookOpen,     color: C.purple,
                items: ["List Courses", "Issue Certificates", "Reach Students", "Generate Leads"] },
            ].map((p, i) => (
              <div key={p.role} className={`rounded-2xl border bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${i === 1 ? "lg:order-3" : i === 2 ? "lg:order-4" : i === 3 ? "lg:order-5" : "lg:order-1"}`}
                style={{ borderColor: C.rule }} data-testid={`ecosystem-${p.role.toLowerCase()}`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-lg grid place-items-center"
                    style={{ background: `${p.color}15`, color: p.color }}><p.icon size={20} /></div>
                  <div className="font-display font-bold uppercase tracking-wide text-sm" style={{ color: p.color }}>{p.role}</div>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {p.items.map((it) => (
                    <li key={it} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} /> {it}</li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Center: AI engine */}
            <div className="lg:order-2 rounded-2xl p-6 text-white text-center grid place-items-center relative overflow-hidden lg:row-span-1"
              style={{ background: `linear-gradient(160deg, ${C.navyDeep} 0%, ${C.navy} 100%)` }}>
              <div className="absolute inset-0 opacity-30"
                style={{ background: `radial-gradient(circle at 50% 50%, ${C.green}33 0%, transparent 60%)` }} />
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-white/10 grid place-items-center mx-auto"><BrainCircuit size={28} style={{ color: C.green }} /></div>
                <div className="mt-4 font-display font-extrabold tracking-tight">SkillConnect</div>
                <div className="font-display font-black text-base mt-0.5" style={{ color: C.green }}>AI Matching Engine</div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-[10px] uppercase tracking-widest font-bold text-white/80">
                  <span>Verified Skills</span><span>Trusted Data</span>
                  <span>Better Matches</span><span>Stronger Outcomes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PLACEMENT METRICS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.green}>Placement metrics</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Real data. Real impact. <span style={{ color: C.green }}>Measurable improvement.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard icon={ClipboardList} value={40} suffix="%+"   label="Profile Completion"        color={C.green}  delay={0} />
            <StatCard icon={ShieldCheck}   value={20} suffix="%+"   label="With Verified Credentials" color={C.navy}   delay={100} />
            <StatCard icon={Sparkles}      value={72} prefix="< " suffix=" hrs" label="Shortlist Time" color={C.purple} delay={200} />
            <StatCard icon={Trophy}        value={10} prefix="5–" label="Hiring Outcomes / Cohort"   color={C.orange} delay={300} />
            <StatCard icon={TrendingUp}    value={60} suffix="%+"   label="Admin Weekly Activity"     color={C.green}  delay={400} />
          </div>
        </div>
      </section>

      {/* ANALYTICS PREVIEW */}
      <section className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.orange}>Analytics & dashboards</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Decide with <span style={{ color: C.green }}>real-time insights.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {/* Placement trend */}
            <Reveal delay={0} className="rounded-2xl border bg-white p-5 float-hover" style={{ borderColor: C.rule }}>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Placement Trend</div>
              <svg viewBox="0 0 200 80" className="w-full h-24 mt-2">
                <AnimatedPoly points="0,55 30,52 60,48 90,40 120,35 150,25 180,18 200,12" color={C.navy}  delay={200} duration={1600} />
                <AnimatedPoly points="0,65 30,60 60,52 90,48 120,38 150,30 180,22 200,16" color={C.green} delay={500} duration={1600} />
              </svg>
              <div className="flex gap-3 text-[10px] font-semibold text-slate-600 mt-1">
                <span><span className="inline-block w-2 h-0.5 align-middle" style={{ background: C.navy }} /> Applications</span>
                <span><span className="inline-block w-2 h-0.5 align-middle" style={{ background: C.green }} /> Placed</span>
              </div>
            </Reveal>

            {/* Top recruiting companies */}
            <Reveal delay={120} className="rounded-2xl border bg-white p-5 float-hover" style={{ borderColor: C.rule }}>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Top Recruiting Companies</div>
              <div className="mt-3 space-y-2">
                {[["TCS",120],["Infosys",98],["Wipro",76],["Accenture",62],["Cognizant",48]].map(([n,v], i) => (
                  <div key={n}>
                    <div className="flex justify-between text-xs font-medium" style={{ color: C.ink }}>
                      <span>{n}</span>
                      <span><CountUp value={v} duration={1300} /></span>
                    </div>
                    <div className="mt-0.5">
                      <AnimatedBar pct={v} max={120} color={C.navy} delay={i * 120} />
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Placement by stream */}
            <Reveal delay={240} className="rounded-2xl border bg-white p-5 float-hover" style={{ borderColor: C.rule }}>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Placement by Stream</div>
              <div className="mt-3 grid place-items-center">
                <AnimatedDonut
                  size={108}
                  stroke={16}
                  segments={[
                    { pct: 62, color: C.navy   },
                    { pct: 18, color: C.green  },
                    { pct: 12, color: C.orange },
                    { pct: 8,  color: C.purple },
                  ]}
                />
              </div>
              <ul className="mt-3 text-[11px] space-y-1">
                <li><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: C.navy }} />Engineering <CountUp value={62} suffix="%" /></li>
                <li><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: C.green }} />Management <CountUp value={18} suffix="%" /></li>
                <li><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: C.orange }} />Science <CountUp value={12} suffix="%" /></li>
                <li><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: C.purple }} />Others <CountUp value={8} suffix="%" /></li>
              </ul>
            </Reveal>

            {/* Student readiness */}
            <Reveal delay={360} className="rounded-2xl border bg-white p-5 text-center float-hover" style={{ borderColor: C.rule }}>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Student Readiness Score</div>
              <div className="relative w-[120px] h-[120px] mx-auto mt-3">
                <AnimatedRing pct={78} size={120} stroke={12} color={C.green} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-display font-black text-3xl" style={{ color: C.green }}>
                    <CountUp value={78} duration={1500} />
                  </span>
                  <span className="text-[10px] text-slate-500 -mt-0.5">/ 100</span>
                </div>
              </div>
              <div className="text-sm font-semibold mt-2" style={{ color: C.green }}>Good</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                <CountUp value={12} prefix="+" suffix="% vs last month" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TECH & SECURITY */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.navy}>Tech & security</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Trust we built into <span style={{ color: C.green }}>every layer.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-12">
            <FeatureCard icon={Lock}        color={C.navy}   title="Secure Data & Encryption"
              body="Data encrypted at rest and in transit using industry-standard ciphers." />
            <FeatureCard icon={KeySquare}   color={C.green}  title="Role-Based Access (RBAC)"
              body="Granular permissions for admins, placement officers, faculty & students." />
            <FeatureCard icon={FileLock2}   color={C.orange} title="Consent & Audit Logs"
              body="Full consent capture and tamper-proof audit trail for every action." />
            <FeatureCard icon={Plug}        color={C.purple} title="API Integrations"
              body="Connect with your ERP, LMS, SSO and HRIS via secure REST APIs." />
            <FeatureCard icon={Cloud}       color={C.navy}   title="Cloud Infrastructure"
              body="Deployed on AWS / Azure with auto-scaling and disaster recovery." />
          </div>
          {/* Trust badges */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              [ShieldCheck, "Enterprise-grade security"],
              [BadgeCheck, "Privacy-first"],
              [ScrollText, "End-to-end encryption"],
              [Sparkles,   "Regular security audits"],
            ].map(([Icon, l], i) => (
              <div key={l} className="rounded-xl border bg-white p-4 flex items-center gap-3" style={{ borderColor: C.rule }} data-testid={`trust-${i}`}>
                <Icon size={20} style={{ color: C.green }} />
                <span className="font-semibold text-sm" style={{ color: C.ink }}>{l}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-500 mt-6">We follow global security standards and privacy-first principles.</p>
        </div>
      </section>

      {/* WHY UNIVERSITIES CHOOSE */}
      <section className="border-y" style={{ background: C.paper, borderColor: C.rule }}>
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.purple}>Why universities choose SkillConnect</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              Six reasons campuses <span style={{ color: C.green }}>switch.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            <FeatureCard icon={TrendingUp}     color={C.green}  title="Higher Placement Conversion"
              body="Improve student outcomes significantly with verified skills + smart matching." />
            <FeatureCard icon={ShieldCheck}    color={C.navy}   title="Verified Talent Pipeline"
              body="Employers trust verified skills — and trust translates to faster offers." />
            <FeatureCard icon={BadgeDollarSign} color={C.orange} title="Save Time & Cost"
              body="Automate manual processes and reduce placement-cell effort by up to 60%." />
            <FeatureCard icon={Library}        color={C.purple} title="Accreditation Ready"
              body="Generate one-click reports for NIRF, NAAC, NBA and accreditation cycles." />
            <FeatureCard icon={Handshake}      color={C.green}  title="Stronger Industry Connect"
              body="Build long-term relationships with quality employers, year after year." />
            <FeatureCard icon={BarChart3}      color={C.navy}   title="Data-Driven Strategy"
              body="Make smart decisions with actionable insights, not gut feel." />
          </div>
        </div>
      </section>

      {/* IMPLEMENTATION & SUPPORT */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Pill color={C.orange}>Implementation & support</Pill>
            <h2 className="font-display font-black text-3xl md:text-5xl mt-3 tracking-tight" style={{ color: C.navyDeep }}>
              90-day pilot. <span style={{ color: C.green }}>Proven results.</span> Long-term partnership.
            </h2>
          </div>
          <div className="mt-12 flex flex-wrap items-start justify-center gap-y-8 gap-x-3">
            {[
              ["01", UserPlus, "Onboard", "We import your data and set up your campus.", C.green],
              ["02", GraduationCap, "Train", "We train your team for smooth adoption.", C.navy],
              ["03", Rocket, "Launch", "Go live with your first placement drive.", C.orange],
              ["04", Trophy, "Succeed", "We support you continuously for better outcomes.", C.purple],
            ].map(([n, Icon, t, d, color], i) => (
              <React.Fragment key={n}>
                <div className="w-full sm:w-[220px] text-center" data-testid={`impl-step-${i}`}>
                  <div className="relative w-16 h-16 mx-auto rounded-2xl grid place-items-center text-white shadow-md transition-transform hover:scale-110"
                    style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}>
                    <Icon size={26} />
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border text-[10px] font-bold grid place-items-center"
                      style={{ borderColor: C.rule, color: C.ink }}>{n}</span>
                  </div>
                  <div className="mt-3 font-bold" style={{ color: C.ink }}>{t}</div>
                  <div className="text-xs text-slate-600 mt-1 leading-snug">{d}</div>
                </div>
                {i < 3 && <Arrow />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <UniversityFAQ />

      {/* FINAL CTA */}
      <section className="text-white relative overflow-hidden" style={{ background: C.navyDeep }}>
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-25 blur-3xl" style={{ background: C.green }} aria-hidden="true" />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: C.orange }} aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-6 py-20 text-center">
          <Pill color={C.green}>Let&apos;s build it together</Pill>
          <h2 className="font-display font-black text-3xl md:text-5xl mt-4 leading-tight">
            LET&apos;S BUILD <span style={{ color: C.green }}>VERIFIED CAREERS</span><br />TOGETHER
          </h2>
          <p className="text-white/70 mt-5 max-w-2xl mx-auto">
            Join SkillConnect and connect students, working professionals, universities, companies, and institutes on one powerful employability platform.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/signup?role=institute" data-testid="final-cta-demo"
              className="px-7 py-4 rounded-lg font-bold text-white inline-flex items-center gap-2 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:scale-[1.02]"
              style={{ background: C.green }}>
              Request a demo today <ArrowUpRight size={18} />
            </Link>
            <a href="mailto:info@skillconnect.com" className="px-7 py-4 rounded-lg font-bold inline-flex items-center gap-2 border-2 border-white/30 text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-1">
              Talk to sales
            </a>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              [ShieldCheck, "Verified Skills", "Build Trust"],
              [Target,      "Better Matches",  "Better Outcomes"],
              [Sparkles,    "Faster Hiring",   "Save Time"],
              [TrendingUp,  "Stronger Placements", "Stronger Futures"],
            ].map(([Icon, t, s]) => (
              <div key={t} className="rounded-xl bg-white/5 border border-white/10 p-4 text-left">
                <Icon size={18} style={{ color: C.green }} />
                <div className="font-bold text-sm mt-2">{t}</div>
                <div className="text-xs text-white/60">{s}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-white/80">
            <div className="flex flex-wrap gap-6">
              <span className="inline-flex items-center gap-2"><Globe size={14} style={{ color: C.green }} /> www.skillconnect.com</span>
              <span className="inline-flex items-center gap-2"><Mail size={14} style={{ color: C.green }} /> info@skillconnect.com</span>
            </div>
            <div className="font-display font-extrabold text-xs tracking-widest uppercase" style={{ color: C.green }}>
              One Ecosystem · Endless Possibilities
            </div>
          </div>
        </div>
      </section>
      {!embedded && <PublicFooter />}
    </div>
  );
}
